// Taşeron portalı — Araç belgesi YÜKLE, KAYIT kapsamlı (Modal + tekil §8 yükleme + §23.2 link).
// api-contract §23.2: `POST /supplier/contractor/vehicles/{vehicleUuid}/documents` `{ type,
// documentUuid, validFrom?, validUntil?, note? }`. Akış (§8 mevcut desen — `documentsApi.uploadDocument`):
//   1. Yetkili bir dosya seçer (ön-doğrulama: `validateDocumentFile` — boyut/tip).
//   2. §8 create → S3 PUT → confirm (`documentsApi.uploadDocument`) → `documentUuid`.
//   3. Araç kaydına link (`linkSupplierVehicleDocument`) — belge `Pending` başlar (İSG uzmanı onaylar).
// İçerik sunucudan geçmez (presigned). Çoklu-dosya/AI analiz kuyruğu (UploadModal) KULLANILMAZ.
//
// **Yetim güvenliği.** §8 yüklemesi başarılı ama link KESİN reddedilirse (4xx) yüklenen belge
// **best-effort** §8.5 ile silinir (asıl hata yine gösterilir). Ağ hatası/5xx'te SİLİNMEZ — bağ
// sunucuda kaydedilmiş olabilir (`documentsApi.linkUploadedDocument`).
//
// **Odak.** Başlangıç odağı GÖRÜNÜR "Dosya seç" düğmesindedir (`initialFocusRef`); gizli
// <input type=file> odaklanamaz. Etiket gizli girdiyi hedefler → düğmenin adı kendi metnidir
// (WCAG 2.5.3); seçilen dosya adı + ipucu/hata `aria-describedby` ile bağlanır.
// Her kapanış yolu (İptal, ✕, Esc, başarı) TEK işleyiciden geçer: mutasyon + form + dosya sıfırlanır.
//
// **Belge türü (GL #7).** Seçici §27 kaydının `vehicle` türleridir (sabit liste YOK; varsayılan yok);
// kaydın durumu formun üstünde (`DocumentTypesStatus`, her açılışta bir kez tazeler). Bitiş tarihi
// türün `expiryRule`'una göre görünür/zorunlu/gizlidir; gizliyken gönderilmez. Sunucu türü reddederse
// (`INVALID`/`INACTIVE`, §27.6) kayıt tazelenir, seçim temizlenir, hata seçicide, odak oraya; yüklenen
// dosya yukarıdaki yetim kuralıyla (4xx) zaten silinmiştir — yeniden kaydetmek temiz bir yüklemedir.
// Form durumu çoklu-grup modallarıyla AYNI kancadadır (`useDocumentFieldGroups`, tek grup).
//
// **KVKK/PII.** İçerik yalnız S3'e gider; UI dosya URL'i/`documentUuid` log'lamaz. Katman: modal.

import {
  Alert,
  Button,
  DOCUMENT_ACCEPT_ATTR,
  Field,
  Input,
  Modal,
  Spinner,
  VEHICLE_NOTE_MAX,
  documentsApi,
  isTypeFieldRejection,
  resolveApiErrorMessage,
  supplierContractorApi,
  validateDocumentFile,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { type RefObject, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invalidateVehicleCaches } from '../../lib/vehicleCache';
import { useDocumentFieldGroups } from '../documents/DocumentFieldGroups';
import {
  DocumentTypeSelect,
  DocumentTypesStatus,
  ValidUntilInput,
  useExpiryRule,
} from '../documents/DocumentTypeFields';

/** Tek alan grubu — `useDocumentFieldGroups` grup kimliği. */
const GROUP = 'vehicle-registry-doc';

interface VehicleDocumentUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number | null;
  vehicleUuid: string;
  /** Aracın plakası — modal açıklamasında bağlam için. */
  vehiclePlate: string;
  /** Açan denetimin ref'i — kapanışta odak buraya döner (menü öğesi DOM'dan kalkar). */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}

export function VehicleDocumentUploadModal({
  open,
  onOpenChange,
  companyId,
  vehicleUuid,
  vehiclePlate,
  restoreFocusRef,
}: VehicleDocumentUploadModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Tür + tarihler + not; tür değişince gizlenen tarih boşaltılır (`setType`, D8); tür reddi + odak.
  const fieldGroups = useDocumentFieldGroups('vehicle');
  const fields = fieldGroups.fieldsOf(GROUP);

  const [file, setFile] = useState<File | null>(null);
  // İstemci ön-doğrulama hata KODU (`DOCUMENT_TOO_LARGE`/`DOCUMENT_TYPE_UNSUPPORTED`) — i18n'de
  // aynı adlı anahtarlar var (`t(code)`); ham anahtar sızmaz.
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chooseRef = useRef<HTMLButtonElement>(null);
  // Eşzamanlı çift Enter/tık: `isPending` bir sonraki render'a kadar görünmez → senkron ref ikinci gönderimi keser.
  const inflightRef = useRef(false);
  const expiryRule = useExpiryRule('vehicle', fields.type);

  function resetAll() {
    setFile(null);
    setFileError(null);
    fieldGroups.reset();
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file || !fieldGroups.isComplete(GROUP)) {
        return; // Kaydet zaten devre dışı — savunma amaçlı.
      }
      // §8 yükleme (create → S3 PUT → confirm) → confirmed dosya (`.uuid` = documentUuid).
      const uploaded = await documentsApi.uploadDocument(file);
      // Link KESİN reddedilirse (4xx) yüklenen dosya yetim kalmasın — best-effort silinir.
      await documentsApi.linkUploadedDocument(uploaded.uuid, () =>
        supplierContractorApi.linkSupplierVehicleDocument(
          vehicleUuid,
          fieldGroups.linkBody(GROUP, uploaded.uuid),
        ),
      );
    },
    // §27.6 — aynı gövde yeniden GÖNDERİLMEZ: kayıt tazelenir, seçim temizlenir, hata seçicide.
    onError: (error) => {
      fieldGroups.handleLinkError(error);
    },
    onSuccess: () => {
      // Yeni belge kayıt listesini ve (aracı gömen) her izin detayını değiştirir.
      invalidateVehicleCaches(queryClient, companyId);
      close();
    },
    onSettled: () => {
      inflightRef.current = false;
    },
  });

  const pending = mutation.isPending;

  /** Tüm kapanış yolları buradan geçer: mutasyon + form durumu + seçilen dosya sıfırlanır. */
  function close() {
    mutation.reset();
    resetAll();
    onOpenChange(false);
  }

  function handleOpenChange(next: boolean) {
    if (next) {
      onOpenChange(true);
    } else if (!pending) {
      close(); // yükleme sürerken kapanmaz (yetim dosya/bayat durum riski)
    }
  }

  function handleFileSelect(selected: File | null) {
    if (!selected) {
      return;
    }
    const invalid = validateDocumentFile(selected);
    if (invalid) {
      setFile(null);
      setFileError(invalid);
      return;
    }
    setFile(selected);
    setFileError(null);
  }

  // Tamamlık = dosya + seçilebilir (aktif) bir tür + kural istiyorsa bitiş tarihi (GL #7).
  const canSubmit = file !== null && fieldGroups.isComplete(GROUP) && !pending;
  const typeRejected = mutation.isError && isTypeFieldRejection(mutation.error);
  const noteTooLong = fields.note.trim().length > VEHICLE_NOTE_MAX;

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      restoreFocusRef={restoreFocusRef}
      title={t('permits.vehicles.upload.title')}
      description={t('permits.vehicles.upload.description', { plate: vehiclePlate })}
      size="sm"
      closeLabel={t('common.close')}
      initialFocusRef={chooseRef}
      footer={
        <>
          <Button variant="secondary" onClick={() => handleOpenChange(false)} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="vehicle-registry-doc-form"
            loading={pending}
            disabled={!canSubmit || noteTooLong}
          >
            {pending ? (
              <Spinner label={t('permits.vehicles.upload.submit')} />
            ) : (
              t('permits.vehicles.upload.submit')
            )}
          </Button>
        </>
      }
    >
      <form
        id="vehicle-registry-doc-form"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit && !noteTooLong && !inflightRef.current) {
            inflightRef.current = true;
            mutation.mutate();
          }
        }}
        noValidate
      >
        {/* Tür reddi seçicinin altındadır — burada ikinci kez gösterilmez. */}
        {mutation.isError && !typeRejected ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}
        <DocumentTypesStatus entity="vehicle" />

        <Field
          htmlFor="vehicle-registry-doc-file"
          label={t('documents.field.file')}
          hint={t('documents.field.fileHint')}
          error={fileError ? t(fileError) : undefined}
          required
        >
          <div className="flex items-center gap-2">
            <Button
              ref={chooseRef}
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={pending}
              aria-invalid={fileError ? true : undefined}
              aria-describedby={`vehicle-registry-doc-file-name vehicle-registry-doc-file-${
                fileError ? 'error' : 'hint'
              }`}
            >
              {t('permits.vehicles.upload.chooseFile')}
            </Button>
            {file ? (
              <span
                id="vehicle-registry-doc-file-name"
                className="inline-flex min-w-0 items-center gap-1.5 text-sm text-fg"
              >
                <Paperclip className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
                <span className="min-w-0 truncate">{file.name}</span>
              </span>
            ) : (
              <span id="vehicle-registry-doc-file-name" className="text-sm text-muted">
                {t('documents.field.filePlaceholder')}
              </span>
            )}
          </div>
          <input
            ref={fileInputRef}
            id="vehicle-registry-doc-file"
            type="file"
            accept={DOCUMENT_ACCEPT_ATTR}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            disabled={pending}
            onChange={(e) => {
              const picked = e.target.files?.[0] ?? null; // önce snapshot
              e.target.value = ''; // sonra reset (aynı dosya tekrar seçilebilsin)
              handleFileSelect(picked);
            }}
          />
        </Field>

        <DocumentTypeSelect
          id="vehicle-registry-doc-type"
          label={t('documents.field.type')}
          entity="vehicle"
          value={fields.type}
          disabled={pending}
          error={fieldGroups.typeError(GROUP)}
          selectRef={fieldGroups.selectRef(GROUP)}
          onChange={(code) => fieldGroups.setType(GROUP, code)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field htmlFor="vehicle-registry-doc-valid-from" label={t('documents.field.validFrom')}>
            <Input
              id="vehicle-registry-doc-valid-from"
              type="date"
              value={fields.validFrom}
              disabled={pending}
              onChange={(e) =>
                fieldGroups.setFields(GROUP, { ...fields, validFrom: e.target.value })
              }
            />
          </Field>
          <ValidUntilInput
            id="vehicle-registry-doc-valid-until"
            label={t('documents.field.validUntil')}
            rule={expiryRule}
            value={fields.validUntil}
            disabled={pending}
            onChange={(value) => fieldGroups.setFields(GROUP, { ...fields, validUntil: value })}
          />
        </div>

        <Field
          htmlFor="vehicle-registry-doc-note"
          label={t('documents.field.note')}
          error={
            noteTooLong ? t('vehicles.upload.noteTooLong', { max: VEHICLE_NOTE_MAX }) : undefined
          }
        >
          <Input
            id="vehicle-registry-doc-note"
            value={fields.note}
            disabled={pending}
            aria-invalid={noteTooLong ? true : undefined}
            aria-describedby={noteTooLong ? 'vehicle-registry-doc-note-error' : undefined}
            onChange={(e) => fieldGroups.setFields(GROUP, { ...fields, note: e.target.value })}
          />
        </Field>
      </form>
    </Modal>
  );
}
